import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError, api } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isReg = mode === "register";

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (isReg) await register(email, password);
      else await login(email, password);
      toast.success(isReg ? "Compte créé." : "Bienvenue.");
      const dest = loc.state?.from || "/";
      nav(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  async function forgot() {
    if (!email) { toast.error("Entrez votre email d'abord."); return; }
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Si un compte existe, un lien de récupération est envoyé (voir logs).");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  }

  return (
    <div className="animate-fade-in pt-6" data-testid={`auth-${mode}-page`}>
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-[color:var(--ivoire)] flex items-center justify-center">
          <Logo size={38} />
        </div>
        <div>
          <p className="font-serif text-lg text-[color:var(--ivoire)] leading-none">Soft Landing</p>
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[color:var(--sauge-light)] mt-1">ASMR</p>
        </div>
      </div>

      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">
        {isReg ? "Créer un compte" : "Bon retour"}
      </h1>
      <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">
        {isReg ? "Inscription rapide pour retrouver vos favoris." : "Connectez-vous pour accéder à votre compte."}
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="text-[10px] font-mono tracking-widest uppercase text-[color:var(--sauge-light)]">Email</label>
          <input
            data-testid="auth-email"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)] font-mono text-sm"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono tracking-widest uppercase text-[color:var(--sauge-light)]">Mot de passe</label>
          <input
            data-testid="auth-password"
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            className="mt-2 w-full h-12 px-4 rounded-xl bg-[#142235]/60 border border-[color:var(--border)] text-[color:var(--ivoire)] font-mono text-sm"
            autoComplete={isReg ? "new-password" : "current-password"}
          />
          {isReg && <p className="text-[11px] text-[color:var(--ivoire-subtle)] mt-1">Au moins 8 caractères.</p>}
        </div>
        {err && <p data-testid="auth-error" className="text-sm text-amber-400">{err}</p>}
        <button
          type="submit" disabled={busy}
          data-testid="auth-submit"
          className="w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium disabled:opacity-60"
        >
          {busy ? "…" : (isReg ? "Créer mon compte" : "Se connecter")}
        </button>
      </form>

      <div className="mt-6 flex items-center justify-between text-sm">
        {isReg ? (
          <Link to="/connexion" data-testid="link-login" className="text-[color:var(--sauge-light)] underline underline-offset-4">
            J'ai déjà un compte
          </Link>
        ) : (
          <Link to="/inscription" data-testid="link-register" className="text-[color:var(--sauge-light)] underline underline-offset-4">
            Créer un compte
          </Link>
        )}
        {!isReg && (
          <button onClick={forgot} data-testid="link-forgot" className="text-[color:var(--ivoire-muted)] underline underline-offset-4">
            Mot de passe oublié
          </button>
        )}
      </div>
    </div>
  );
}
